import { Stack, Title, Text, Group, Button, Paper, Radio, ThemeIcon, Textarea, Chip, Alert, Collapse } from '@mantine/core';
import { IconPalette, IconArrowLeft, IconArrowRight, IconSparkles, IconAlertCircle } from '@tabler/icons-react';
import { useState } from 'react';
import { FlavorProfile } from '../../types/familyProfile';
import { HouseholdSegment, bySegment } from '../../utils/householdSegment';

type FlavorProfileStepProps = {
  value: { flavorProfile: FlavorProfile };
  onChange: (value: { flavorProfile: FlavorProfile }) => void;
  onNext: () => void;
  onBack: () => void;
  isLastStep: boolean;
  isCompleting?: boolean;
  householdSegment?: HouseholdSegment;
};

export const FlavorProfileStep = ({
  value,
  onChange,
  onNext,
  onBack,
  isLastStep,
  householdSegment,
}: FlavorProfileStepProps) => {
  const [preferencesText, setPreferencesText] = useState(value?.flavorProfile?.preferences || '');
  const [showWarning, setShowWarning] = useState(false);

  // Set default flavor intensity if not already set
  if (!value.flavorProfile?.flavorIntensity) {
    onChange({ flavorProfile: { ...value.flavorProfile, flavorIntensity: 'moderate' } });
  }

  const flavorChips = [
    { label: 'Authentic', text: 'We prefer traditional, authentic homestyle recipes over Americanized versions', priority: 'high' },
    { label: 'Fusion & modern', text: 'We enjoy fusion cuisines, Americanized versions, and modern twists on traditional recipes', priority: 'high' },
    { label: 'Quick & simple', text: 'We value quick, simple, and unfussy meals over complex restaurant-style cooking (practical recipes rather than gourmet)', priority: 'high' },
    { label: 'Gourmet', text: 'We love restaurant-quality flavors and techniques at home and reach for more gourmet recipes', priority: 'high' },
    { label: 'Balanced', text: "We're all about quality ingredients and balance, not strict diets or rules, because healthy food shouldn't be boring", priority: 'high' },
    { label: 'Health-conscious', text: 'We gravitate toward meals that are nutrient-rich and focused on whole foods, with an emphasis on fresh vegetables, lean proteins, and complex carbohydrates', priority: 'high' },
    { label: 'Bold seasoning', text: "We appreciate seasoning that's anything but boring - the bolder, the better", priority: 'medium' },
    { label: 'Mild flavors', text: 'We favor gentle, subtle seasoning and mild flavors over bold or intense tastes', priority: 'medium' },
    { label: 'Moderate heat', text: 'We go for moderate spice levels - not too mild, not too hot', priority: 'medium' },
  ];

  const handleFlavorIntensityChange = (flavorIntensity: 'mild' | 'moderate' | 'bold') => {
    onChange({ flavorProfile: { ...value.flavorProfile, flavorIntensity } });
  };

  const handlePreferencesChange = (preferences: string) => {
    setPreferencesText(preferences);
    onChange({ flavorProfile: { ...value.flavorProfile, preferences: preferences.trim() || undefined } });
    // Hide warning when user starts typing
    if (showWarning && preferences.trim().length > 0) {
      setShowWarning(false);
    }
  };

  const handleChipClick = (chipText: string) => {
    const currentText = preferencesText.trim();
    
    // Check if this chip's text is already in the preferences
    if (currentText.includes(chipText)) {
      // Remove the chip text
      let newText = currentText.replace(chipText, '');
      // Clean up extra periods and spaces
      newText = newText.replace(/\.\s*\./g, '.').replace(/^\.\s*/, '').replace(/\s*\.\s*$/, '').trim();
      // Clean up multiple spaces
      newText = newText.replace(/\s+/g, ' ');
      handlePreferencesChange(newText);
    } else {
      // Add the chip text
      const newText = currentText 
        ? `${currentText}. ${chipText}`
        : chipText;
      handlePreferencesChange(newText);
    }
  };

  const isChipSelected = (chipText: string) => {
    return preferencesText.includes(chipText);
  };

  const handleNext = () => {
    // Check if preferences text is empty or very short
    const hasMinimalText = preferencesText.trim().length < 10;
    
    if (hasMinimalText && !showWarning) {
      setShowWarning(true);
      // Scroll to warning message
      setTimeout(() => {
        document.querySelector('[data-warning]')?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }, 100);
    } else {
      onNext();
    }
  };

  const getMobileChips = () => flavorChips.filter(chip => chip.priority === 'high');
  const getAllChips = () => flavorChips;

  const getFlavorIntensityDescription = (intensity: string) => {
    switch (intensity) {
      case 'mild':
        return 'Simple seasonings, gentle flavors, comfort food approach';
      case 'moderate':
        return 'Well-seasoned with balanced, developed flavors';
      case 'bold':
        return 'Robust seasonings, complex flavor profiles, restaurant-quality depth';
      default:
        return '';
    }
  };

  return (
    <Stack gap="xl" w={{ base: '100%', sm: 700 }} mx="auto" px={{ base: 'sm', sm: 'md' }}>
      {/* Header */}
        <Stack gap="md" ta="center" align="center">
          <ThemeIcon size={80} radius="xl" variant="gradient" gradient={{ from: 'orange', to: 'red' }}>
            <IconPalette size={40} />
          </ThemeIcon>
          <Stack gap="xs" align="center">
            <Title order={2} size="h1" fw={700} c="dark">
              Flavor profile
            </Title>
            <Text size="lg" c="dimmed" ta="center" maw={450}>
              {bySegment(householdSegment, {
                family:
                  'Help us understand your flavor preferences to create recipes with the perfect taste profile for your family',
                household:
                  'Help us understand your flavor preferences to create recipes with the perfect taste profile for your household',
                solo: 'Help us understand your flavor preferences to create recipes with the perfect taste profile for you',
              })}
            </Text>
          </Stack>
        </Stack>

      {/* Content */}
        <Paper 
          radius="lg" 
          p={{ base: 'md', sm: 'xl' }}
          style={{
            border: '1px solid var(--mantine-color-gray-2)',
            width: '100%'
          }}
        >
          <Stack gap="xl">
            {/* Flavor Intensity */}
            <Stack gap="md">
              <Text fw={600} size="lg">How bold do you like your flavors?</Text>
              <Text size="sm" c="dimmed">This affects seasoning depth, flavor complexity, and cooking techniques</Text>
              
              <Stack gap="sm">
                {(['mild', 'moderate', 'bold'] as const).map((intensity) => (
                  <Paper 
                    key={intensity}
                    p="md" 
                    radius="lg" 
                    style={{ 
                      cursor: 'pointer',
                      border: value.flavorProfile?.flavorIntensity === intensity ? '1px solid var(--mantine-color-orange-6)' : '1px solid var(--mantine-color-gray-3)',
                      background: value.flavorProfile?.flavorIntensity === intensity ? 'var(--mantine-color-orange-0)' : 'white',
                      transition: 'all 0.2s ease',
                    }}
                    onClick={() => handleFlavorIntensityChange(intensity)}
                  >
                    <Group gap="md" align="flex-start">
                      <Radio
                        checked={value.flavorProfile?.flavorIntensity === intensity}
                        onChange={() => handleFlavorIntensityChange(intensity)}
                        color="orange"
                        size="md"
                        styles={{ 
                          radio: { cursor: 'pointer' },
                          body: { cursor: 'pointer' },
                          inner: { cursor: 'pointer' }
                        }}
                      />
                      <Stack gap="xs" style={{ flex: 1 }}>
                        <Group gap="xs" align="center">
                          <Text fw={500} tt="capitalize">{intensity}</Text>
                          {intensity === 'bold' && <IconSparkles size={16} color="var(--mantine-color-orange-6)" />}
                        </Group>
                        <Text size="sm" c="dimmed">
                          {getFlavorIntensityDescription(intensity)}
                        </Text>
                      </Stack>
                    </Group>
                  </Paper>
                ))}
              </Stack>
            </Stack>

            {/* Additional Preferences */}
            <Stack gap="md">
              <Group gap="xs" align="center">
                <Text fw={600} size="lg">Describe your taste preferences</Text>
                <Text size="sm" c="orange.6" fw={500}>(The more detail, the better!)</Text>
              </Group>
              
              <Text size="sm" c="dimmed">
                {bySegment(householdSegment, {
                  family: 'Select any that sound like your family, or write your own:',
                  household: 'Select any that sound like your household, or write your own:',
                  solo: 'Select any that sound like you, or write your own:',
                })}
              </Text>
              
              {/* Flavor Chips - Mobile (5 chips) */}
              <Group gap="xs" hiddenFrom="sm">
                {getMobileChips().map((chip, index) => (
                  <Chip
                    key={index}
                    checked={isChipSelected(chip.text)}
                    onChange={() => handleChipClick(chip.text)}
                    color="orange"
                    size="sm"
                    style={{ cursor: 'pointer' }}
                  >
                    {chip.label}
                  </Chip>
                ))}
              </Group>

              {/* Flavor Chips - Desktop (all chips) */}
              <Group gap="xs" visibleFrom="sm">
                {getAllChips().map((chip, index) => (
                  <Chip
                    key={index}
                    checked={isChipSelected(chip.text)}
                    onChange={() => handleChipClick(chip.text)}
                    color="orange"
                    size="sm"
                    style={{ cursor: 'pointer' }}
                  >
                    {chip.label}
                  </Chip>
                ))}
              </Group>
              
              <Textarea
                placeholder={bySegment(householdSegment, {
                  family: 'Tell us what makes food delicious to your family...',
                  household: 'Tell us what makes food delicious to your household...',
                  solo: 'Tell us what makes food delicious to you...',
                })}
                value={preferencesText}
                onChange={(event) => handlePreferencesChange(event.currentTarget.value)}
                minRows={8}
                maxRows={15}
                maxLength={1000}
                size="md"
                styles={{
                  input: {
                    fontSize: '14px',
                    lineHeight: 1.5,
                    minHeight: '120px'
                  }
                }}
              />

              {/* Warning Alert */}
              <Collapse in={showWarning}>
                <Alert
                  icon={<IconAlertCircle size={16} />}
                  color="yellow"
                  variant="light"
                  radius="lg"
                  data-warning
                  styles={{
                    root: {
                      marginTop: '12px'
                    }
                  }}
                >
                  <Text size="sm">
                    Consider sharing a few taste preferences to get more personalized meals. You can always update this later in your profile.
                  </Text>
                </Alert>
              </Collapse>
            </Stack>
          </Stack>
        </Paper>

      {/* Summary */}
      {(value.flavorProfile?.flavorIntensity || value.flavorProfile?.preferences) && (
        <Paper 
          p="lg" 
          radius="lg" 
          bg="gray.0" 
          style={{ 
            border: '1px solid var(--mantine-color-gray-2)',
            minHeight: 80,
            width: '100%'
          }}
        >
          <Group gap="md" align="center" style={{ height: '100%' }}>
            <ThemeIcon 
              size={40} 
              radius="lg" 
              color="green" 
              variant="light"
            >
              <IconPalette size={20} />
            </ThemeIcon>
            
            <Stack gap={4} style={{ flex: 1, minWidth: 0 }}>
              <Text fw={600} c="green.7" style={{ wordBreak: 'break-word' }}>
                Flavor profile saved!
              </Text>
              <Text size="sm" c="dimmed" style={{ wordBreak: 'break-word' }}>
                {value.flavorProfile?.flavorIntensity && (
                  <>Intensity: {value.flavorProfile.flavorIntensity}</>
                )}
                {value.flavorProfile?.flavorIntensity && value.flavorProfile?.preferences && <br />}
                {value.flavorProfile?.preferences && (
                  <>Additional preferences provided</>
                )}
              </Text>
            </Stack>
          </Group>
        </Paper>
      )}

      {/* Navigation */}
      <Group justify="space-between" pt="md" wrap="nowrap" className="wizard-footer">
        <Button 
          variant="subtle" 
          size="md"
          leftSection={<IconArrowLeft size={18} />}
          onClick={onBack}
          color="orange"
        >
          Back
        </Button>
        
        <Button 
          size="md"
          rightSection={<IconArrowRight size={18} />}
          onClick={handleNext}
          color="orange"
          variant="filled"
        >
          {isLastStep ? 'Finish' : 'Continue'}
        </Button>
      </Group>
    </Stack>
  );
};
